/**
 * Web Push helpers for the admin notification toggle.
 *
 * iOS caveat worth knowing: Safari only allows push for sites the user has
 * added to their Home Screen. In a normal iPhone browser tab `Notification`
 * is undefined, which `pushSupported()` reports so the UI can explain why.
 */

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY ?? '';

export type PushSupport = 'ok' | 'unsupported' | 'ios-needs-home-screen' | 'not-configured';

export function pushSupport(): PushSupport {
  if (!VAPID_PUBLIC_KEY) return 'not-configured';

  const hasApi =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (hasApi) return 'ok';

  const isApple = /iPhone|iPad|iPod/.test(navigator.userAgent);
  // Standalone means it was launched from the Home Screen.
  const standalone = (window.navigator as { standalone?: boolean }).standalone === true;
  if (isApple && !standalone) return 'ios-needs-home-screen';

  return 'unsupported';
}

/** VAPID keys travel as base64url; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const normalized = padded.replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

async function register(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
}

/** Returns the subscription to send to the server, or null if declined. */
export async function subscribeToPush(): Promise<PushSubscriptionJSON | null> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const registration = await register();
  await navigator.serviceWorker.ready;

  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing.toJSON();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });

  return subscription.toJSON();
}

export async function unsubscribeFromPush(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  const subscription = await registration?.pushManager.getSubscription();
  await subscription?.unsubscribe();
}
